import { Component, EventEmitter, Input, OnInit, Output, Renderer2 } from '@angular/core';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';
import { InvoiceService } from 'src/app/data/service/invoice.service';
import { ToasterMessageService } from 'src/app/shared/toaster-message/toaster-message.service';
import { Invoice } from '../../models/invoice/invoice';
import { RecognizedInvoiceService } from '../../services/recognized-invoice.service';
import { OrganizationService } from 'src/app/data/service/organization.service';
@Component({
  selector: 'app-upload-document',
  // The same viewer as everywhere else a document is previewed (the dashboard,
  // Bills to Pay): title bar with Download, the page on a grey ground. Only how
  // the file is found differs — this one follows the invoice open in the form.
  templateUrl: '../invoice-preview-web/invoice-preview-web.component.html',
  styleUrls: ['../invoice-preview-web/invoice-preview-web.component.scss']
})
export class UploadDocumentComponent extends BaseComponent implements OnInit {
  @Input() isHorizontalDirection: boolean = false;
  // Fires when the bill has no document (no pdfUrl, or the file can't be
  // fetched) so the host can collapse the preview instead of showing grey.
  @Output() documentUnavailable = new EventEmitter<void>();

  /** Read by the shared template's title bar. */
  invoice: Invoice = null;
  /** The shared template's error state. This host collapses the preview instead
   *  (documentUnavailable), so it stays null. */
  previewError: string | null = null;
  /** The panel beside it has its own close. */
  showClose: boolean = false;

  private invoicePdfUrl: string = '';
  private fileName: string = '';
  isLoaded: boolean = false;
  // Whether the current bill actually has a document — gates the loading
  // placeholder so a bill with no attachment shows no loading animation.
  hasPdf: boolean = false;
  isPdf: boolean = false;
  tempFile: string;
  showToolTipDelay: number = 400;
  pageScale: number = 100;

  constructor(
    private _toasterMessageService: ToasterMessageService,
    private _invoiceService: InvoiceService,
    private _organizationService: OrganizationService,
    private _recognizedInvoiceService: RecognizedInvoiceService,
    private renderer: Renderer2,
  ) {
    super();
  }

  ngOnInit() {
    this.subscribeToFileUpdates();
  }

  subscribeToFileUpdates() {
    this._recognizedInvoiceService.existingInvoiceState$
      .pipe(this.untilThis)
      .subscribe(
        (invoice: Invoice) => {
          this.isLoaded = false;
          this.invoice = invoice;
          this.invoicePdfUrl = invoice.pdfUrl;
          this.hasPdf = !!invoice.pdfUrl;
          this.fileName = `${invoice.supplierName} ${invoice.invoiceNumber}`;
          // No document attached — don't hit the download endpoint (it 500s on
          // a null file) and don't show the loading placeholder.
          if (!invoice.pdfUrl) {
            this.documentUnavailable.emit();
            return;
          }
          this.downloadInvoice(invoice.pdfUrl,this._organizationService.selectedOrganization);
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      );
  }

  downloadInvoice(id: string,organisationId:string) {
    this._invoiceService.downloadFile(id,organisationId)
      .pipe(this.untilThis)
      .subscribe(
        (blob: Blob) => {
          if (!blob || !blob.size) {
            this.documentUnavailable.emit();
            return;
          }
          this._setPreview(blob);
        },
        () => {
          // The file couldn't be fetched (e.g. the bill has no stored document):
          // collapse the preview rather than surfacing an error toast.
          this.documentUnavailable.emit();
        }
      )
  }

  private _setPreview(blob: Blob) {
    const reader = new FileReader();
    reader.onloadend = () => {
      this.tempFile = reader?.result as string;
      this.isPdf = (blob.type === "application/pdf" || blob.type === "application/octet-stream") ? true : false;
      this.isLoaded = true;
    }

    reader.readAsDataURL(blob);
  }

  downloadPdfInvoice() {
    const link = this.renderer.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute('href', this._invoiceService.getDownloadInvoiceUrl(this.invoicePdfUrl, "", this.fileName));
    link.setAttribute('download', this.invoicePdfUrl);
    link.click();
    link.remove();
  }

  /** Part of the shared template; not shown here (showClose is false). */
  ClosePreviewSidebar() { }

  /** One wheel notch in (+1) or out (-1), between 50% and 400%. */
  public zoomBy(direction: number)
	{
    this.pageScale = Math.min(400, Math.max(50, this.pageScale + direction * 25));
	}

  public zoomIn()
	{
    this.pageScale = this.pageScale + 25;
	}

  public zoomReset()
	{
    this.pageScale = 100;
	}

	public zoomOut()
	{
    if (this.pageScale > 100) {
      this.pageScale = this.pageScale - 25;
    }
	}
}
